import type { TFunction } from "i18next"
import fp from "lodash/fp"

import type { EntityType } from "types/common.types"
import type { PlotTargetEntity, QueryPlotDef, RecordType } from "types/dashboard.types"
import type { DeviceSettings } from "types/device.types"
import type { TowerSettings } from "types/towers.types"

import {
  deviceTabPlotDefs,
  getTowerTabPlotDefs,
  positionInTowerPlotDef,
  legacyPositionInTowerPlotDef,
  positionInPowerlinePlotDef,
  powerlineInfoTabPlotDefs,
  getSpanTabPlotDefs,
  getTowerSegmentsTabPlotDefs,
  legacyTowerInfoTabPlotDefs,
  towerTabPlotDefs,
  wStationAmbientTabPlotDefs,
} from "helpers/config/dashboards"
import {
  AMPACITY_FORECAST_AAR_FIELD,
  AMPACITY_FORECAST_DLR_FIELD,
  BATTERY_LEVEL_FIELD,
  BATTERY_STATUS_FIELD,
  LAT_FIELD,
  LON_FIELD,
  MOTION_ALERT_FIELD,
  NET_NAME_FIELD,
  NET_SIGNAL_FIELD,
  NET_TRANSPORT_FIELD,
  SPAN_AMPACITY_AAR_FIELD,
} from "helpers/config/telemetryFieldDefs"
import { getFieldUnit } from "./telemetryFields"
import { getTowerSegments } from "./towerHealth"
import { getTelemetryTranslation } from "./translations"

// Labels carry the stored unit, not the display one: it is the unit an alert threshold is
// entered and evaluated in.
export const getTelemetryWithUnit = (name: string, t: TFunction): string => {
  const label = getTelemetryTranslation(name, t)
  const unit = getFieldUnit(name)
  return unit ? `${label} [${unit}]` : label
}

export const formatSelectTelemetries = (
  telemetries: string[],
  t: TFunction,
): { label: string; value: string }[] =>
  fp.flow(
    fp.map((telemetry: string) => {
      return {
        label: getTelemetryWithUnit(telemetry, t),
        value: telemetry,
      }
    }),
    fp.sortBy(({ label }) => label.toLowerCase()),
  )(telemetries || [])

export const getSignalTransportType = (value: number): string => {
  if ((256 & value) !== 0) {
    return "5g"
  }
  if ((128 & value) !== 0) {
    return "4g"
  }
  if ((64 & value) !== 0) {
    return "3g"
  }
  if ((32 & value) !== 0) {
    return "2g"
  }
  if ((16 & value) !== 0) {
    return "wifi"
  }
  if ((8 & value) !== 0) {
    return "lora"
  }
  if ((2 & value) !== 0) {
    return "sms"
  }
  return "mobile"
}

export const formatSignalStatusData = (signalStatusData: RecordType[]): RecordType[] => {
  const netSignal = signalStatusData?.find((d) => d._field === NET_SIGNAL_FIELD)
  const netName = signalStatusData?.find((d) => d._field === NET_NAME_FIELD)
  const netTransport = signalStatusData?.find((d) => d._field === NET_TRANSPORT_FIELD)

  const getLegend = (value: number | undefined): string | undefined => {
    if (value === undefined) {
      return undefined
    }
    const transport = getSignalTransportType(value)
    return `net_transport.${transport}`
  }

  const legend = getLegend(netTransport?._value as number | undefined)

  const legendBlock = legend
    ? {
        [NET_NAME_FIELD]: netName?._value,
        [NET_TRANSPORT_FIELD]: netTransport?._value,
        legend,
      }
    : undefined

  if (netSignal) {
    return [
      {
        ...netSignal,
        ...legendBlock,
      },
    ]
  }
  return []
}

export const formatBatteryStatusData = (batteryData: RecordType[]): RecordType[] => {
  const batteryValue = batteryData?.find(
    (d: RecordType) => d._field === BATTERY_LEVEL_FIELD,
  )
  const batteryStatus = batteryData?.find(
    (d: RecordType) => d._field === BATTERY_STATUS_FIELD,
  )

  const getLegend = (value: number): string => {
    if ((value & 2) !== 0) {
      return "Charging"
    }
    return "Discharging"
  }

  const legend =
    batteryStatus?._value !== undefined
      ? getLegend(batteryStatus._value as number)
      : undefined

  if (batteryValue) {
    return [
      {
        ...batteryValue,
        legend,
      },
    ]
  }
  return []
}

const irrelevantTelemetries = new Set([
  MOTION_ALERT_FIELD,
  NET_TRANSPORT_FIELD,
  NET_NAME_FIELD,
  BATTERY_STATUS_FIELD,
  LAT_FIELD,
  LON_FIELD,
  AMPACITY_FORECAST_AAR_FIELD,
  AMPACITY_FORECAST_DLR_FIELD,
  SPAN_AMPACITY_AAR_FIELD,
])

const getDefsTelemetries = (defs: QueryPlotDef[], entity: PlotTargetEntity): string[] =>
  fp.flow(
    fp.filter(
      (def: QueryPlotDef) =>
        def.dataSource?.entity === entity && def.dataSource?.service !== "weather",
    ),
    fp.flatMap((def: QueryPlotDef) => def.reqConfig?.fields),
    fp.compact,
    fp.reject((field) => irrelevantTelemetries.has(field)),
    fp.uniq,
  )(defs)

// Every entity resolves its telemetries from a different source, so the extras are optional
// and named per entity: a caller only passes what its own entity needs.
interface EntityTelemetriesOptions {
  deviceType?: string
  deviceSettings?: DeviceSettings
  towerSettings?: TowerSettings
}

export const getEntityTelemetries = (
  entity: EntityType,
  { deviceType, deviceSettings, towerSettings }: EntityTelemetriesOptions = {},
): string[] => {
  const installedOnTower = deviceSettings?.installation_type === "tower"
  const hasWeatherStation = !!deviceSettings?.features?.weather?.was_enabled
  const deviceTowerHealthEnabled =
    deviceSettings?.features?.tower_health?.enabled === true

  switch (entity) {
    case "powerline":
      return getDefsTelemetries(powerlineInfoTabPlotDefs, "powerline")
    case "group":
      return []
    case "tower": {
      if (towerSettings?.features?.tower_health?.enabled === true) {
        return getDefsTelemetries(
          [
            ...towerTabPlotDefs,
            ...getTowerSegmentsTabPlotDefs(getTowerSegments(towerSettings)),
          ],
          "tower",
        )
      }
      return getDefsTelemetries(legacyTowerInfoTabPlotDefs, "tower")
    }
    case "device":
    default:
      switch (deviceType) {
        case "weather_station":
          return getDefsTelemetries(
            [...wStationAmbientTabPlotDefs, ...deviceTabPlotDefs],
            "device",
          )
        case "sentrisense":
        default:
          if (installedOnTower) {
            const positionDef = deviceTowerHealthEnabled
              ? positionInTowerPlotDef
              : legacyPositionInTowerPlotDef
            const towerDefs = [
              ...getTowerTabPlotDefs(deviceSettings?.features ?? {}),
              positionDef,
              ...deviceTabPlotDefs,
            ]
            return getDefsTelemetries(
              hasWeatherStation
                ? [...towerDefs, ...wStationAmbientTabPlotDefs]
                : towerDefs,
              "device",
            )
          }
          return getDefsTelemetries(
            [
              positionInPowerlinePlotDef,
              ...getSpanTabPlotDefs(deviceSettings?.features ?? {}),
              ...deviceTabPlotDefs,
            ],
            "device",
          )
      }
  }
}
